import "./style.scss";

import AlertTriangle from "assets/img/svg/alertTriangle";
import Basket from "assets/img/svg/basket";
import { Info } from "assets/img/svg/info";
import Plus from "assets/img/svg/plus";
import MessageBlock from "components/shared/MessageBlock";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import type { ITermFormData } from "containers/TermFormContainer";
import { ITermFormValues } from "containers/TermFormContainer";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useRef, useState } from "react";
import { useFormContext, UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";

import { isMilestoneThresholdAmountBased } from "../milestoneBaseColumnUtils";

interface Milestone {
    percentage: number | string;
    quantity: number | string;
}

interface IMilestonesInputs {
    /**
     * Whether channel accepts milestones or nots.
     */
    hasMilestones: boolean;
    /**
     * The channel and its data.
     */
    channel: IChannel | undefined;
    /**
     * Contract-scoped column used for milestone thresholds (drives "ventes" vs currency unit).
     */
    milestoneBaseColumn: CalculationBaseColumn;
    /**
     * Triggers saving the changes within the input inside the channel data (handled by parent)
     */
    onChange: (newChannel: any) => void;
    /**
     * (on edit mode) Data already saved within channel for each milestone.
     */
    propMilestones?: { percentage: number | string; quantity: number | string }[];
    /**
     * Whether the form is on new or edit
     */
    mode: "edit" | "new";
    /** error paints the whole milestone line (labels and inputs) in danger color. */
    variant?: "normal" | "error";
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
}

/**
 * This component handles the rendering and modifications of each milestone line. These lines can be many or just one if there are no milestones.
 */
const MilestonesInput = ({
    hasMilestones,
    channel,
    milestoneBaseColumn,
    onChange,
    propMilestones,
    variant = "normal",
    useFormData,
}: IMilestonesInputs) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;
    const { currencySymbol } = useCompanySettings();

    const [milestones, setMilestones] = useState<Milestone[]>(
        propMilestones ?? [{ percentage: "", quantity: Infinity }],
    );
    const [isChangeValid, setIsChangeValid] = useState(true);
    const [percentageHigherThan100, setPercentageHigherThan100] = useState(false);

    const percentageInput = useRef<HTMLInputElement>(null);

    const handleAddMilestone = () => {
        const upcomingMilestoneEnding =
            milestones.length <= 1 ? 1000 : +milestones[milestones.length - 2]?.quantity + 1000;
        const lastMilestone = milestones.length <= 1 ? { ...milestones[0] } : { ...milestones[milestones.length - 1] };

        lastMilestone.quantity = upcomingMilestoneEnding;

        const newMilestones = [
            ...milestones.slice(0, milestones.length - 1),
            lastMilestone,
            {
                percentage: lastMilestone?.percentage,
                quantity: Infinity,
            },
        ];

        setMilestones(newMilestones);
        onChange({ channelId: channel?.id, data: newMilestones });
    };

    const handleValueChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number,
        inputType: "percentage" | "quantity",
    ) => {
        // This line handles commas and stops from writing anything else than digits:
        const newValue: string | number = e.target.value.replace(",", ".").replace(/[A-zÀ-ú"'/\-_`!;?^$]/, "");

        // To show message when percentage is higher than 100
        if (inputType === "percentage" && +newValue > 100) {
            setPercentageHigherThan100(true);
        } else {
            setPercentageHigherThan100(false);
        }

        // This replaces value in the current milestone:
        let currentMilestone = milestones[index];
        currentMilestone = {
            ...currentMilestone,
            [inputType]: newValue !== "" ? newValue : "",
        };

        // This updates milestones array with new milestone and updates the start value of next milestone if changing the end value:
        const newMilestones = milestones.map((mile, idx) => {
            if (idx === index) {
                return currentMilestone;
            } else if (idx === index + 1) {
                return { ...mile };
            } else {
                return mile;
            }
        });

        // This checks whether value changed makes sense with other milestones values:
        const isNotValid = newMilestones.some((mile, idx) => {
            if (mile.quantity !== Infinity) {
                return +mile?.quantity <= +newMilestones[idx - 1]?.quantity + 1;
            }
        });

        if (isNotValid) {
            setError && setError("channels", { message: `Milestone error on channel ${channel?.id}` });
        } else {
            clearErrors && clearErrors("channels");
        }

        setIsChangeValid(!isNotValid);
        setMilestones(newMilestones);
        onChange({ channelId: channel?.id, data: newMilestones });
    };

    /**
     * Handles deletion of milestone. Deletes only last milestone.
     */
    const handleDeleteMilestone = () => {
        const withLastDeleted = milestones.filter((item) => item.quantity !== Infinity);
        const newMilestones = withLastDeleted.map((mile, index) => {
            if (index === withLastDeleted.length - 1) {
                return { ...mile, quantity: Infinity };
            } else {
                return mile;
            }
        });

        setMilestones(newMilestones);
        onChange({ channelId: channel?.id, data: newMilestones });
    };

    return (
        <>
            <div
                className={`milestones-inputs${variant === "error" ? " milestones-inputs--error" : ""}${
                    !isChangeValid || percentageHigherThan100 ? " warning" : ""
                }`}
                id={`milestone-inputs-${channel?.id}`}
            >
                {milestones.map((mile, index) => {
                    const previousIterationMilestone = index > 0 ? +milestones[index - 1]?.quantity + 1 : 0;
                    const isLastLine = mile.quantity === Infinity || mile.quantity === "Infinity";
                    const mileQuantity = mile.quantity !== Infinity && mile?.quantity;
                    const isAmountBasedThreshold = isMilestoneThresholdAmountBased(milestoneBaseColumn);
                    return (
                        <div
                            className="milestone-line"
                            key={`milestone-${index}`}
                            style={{
                                paddingRight: `${!isLastLine ? "1.45rem" : "0px"}`,
                                marginBottom: `${
                                    !isLastLine || hasMilestones
                                        ? "1rem"
                                        : index === 0 && !hasMilestones
                                          ? "0.5rem"
                                          : "O.5rem"
                                }`,
                                marginTop: `${index === 0 && "0.5rem"}`,
                            }}
                        >
                            <div className="left-side">
                                <span>{previousIterationMilestone ?? 0}</span>
                                <span className="text-grey">
                                    {isLastLine &&
                                        (isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales"))}{" "}
                                    {t("MilestonesForm.To")}
                                </span>
                                {isLastLine ? (
                                    <span>{t("MilestonesForm.InfinityText")}</span>
                                ) : (
                                    <StyledTextField
                                        className={`quantity ${isChangeValid ? "" : "red"}`}
                                        size="small"
                                        inputType="number"
                                        id={`channel-${channel?.id}-milestone-${index}`}
                                        value={!hasMilestones ? "l'infini" : mileQuantity ?? 0}
                                        onChange={(e: any) => handleValueChange(e, index, "quantity")}
                                        placeholder="1000"
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                    />
                                )}
                                {!isLastLine && (
                                    <span className="text-grey">
                                        {isAmountBasedThreshold ? currencySymbol : t("MilestonesForm.Sales")}
                                    </span>
                                )}
                            </div>
                            <div className="right-side">
                                {channel?.calculationMethod === CalculationMethod.PROPORTIONAL ||
                                channel?.calculationMethod === CalculationMethod.SUBSCRIPTION ? (
                                    <StyledTextField
                                        className="percentage"
                                        size="small"
                                        id={`channel-${channel?.id}-percentage-${index}`}
                                        value={mile.percentage ?? 0}
                                        placeholder="0"
                                        onChange={(e: any) => handleValueChange(e, index, "percentage")}
                                        trueInputRef={percentageInput}
                                        inputType="number"
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                    />
                                ) : (
                                    <span>100</span>
                                )}
                                <span className="text-grey">{t("MilestonesForm.Percentage")}</span>
                                {isLastLine && index !== 0 && <Basket onClick={() => handleDeleteMilestone()} />}
                            </div>
                        </div>
                    );
                })}
                {hasMilestones && (
                    <button
                        type="button"
                        className="new-milestone-btn"
                        id={`new-milestone-channel-${channel?.id}-btn`}
                        onClick={() => handleAddMilestone()}
                    >
                        <Plus /> {t("MilestonesForm.NewMilestone")}
                    </button>
                )}
                {!isChangeValid && <MessageBlock text={t("MilestonesForm.ErrorSteps")} type="error" />}
                {percentageHigherThan100 && <MessageBlock type="error" text={t("MilestonesForm.ErrorPercentage")} />}
                {percentageInput?.current?.value === "100" && (
                    <MessageBlock type="warning" text={t("ConnectTitle.PercentageWarning")} />
                )}
            </div>
        </>
    );
};

export default MilestonesInput;
